import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  Switch,
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { delftBlue } from "@/theme/colors";
import { getSwitchStyles } from "@/utils/switchStyles";
import FormInput from "@/components/forms/FormInput";
import { useCreateGuideMutation } from "@/app/features/dashboard/dashboardApiSlice";
import { useSelector } from "react-redux";
import { selectUser } from "@/app/features/auth/authSlice";

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Must be a valid email address")
    .required("Email is required"),
  username: Yup.string()
    .min(2, "Username must be at least 2 characters")
    .max(50, "Username cannot exceed 50 characters")
    .matches(
      /^[a-zA-Z0-9_]+$/,
      "Username can only contain letters, numbers, and underscores"
    )
    .required("Username is required"),
  fullName: Yup.string()
    .max(100, "Full name cannot exceed 100 characters")
    .optional(),
  phoneNumber: Yup.string()
    .max(10, "Phone number cannot exceed 10 characters")
    .optional(),
  isActive: Yup.boolean(),
});

const GuideFormPage = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [createGuide, { isLoading: isCreating }] = useCreateGuideMutation();

  const initialValues = {
    email: "",
    username: "",
    fullName: "",
    phoneNumber: "",
    isActive: true,
  };

  const handleSubmit = async (values, { setStatus, setSubmitting }) => {
    try {
      // For Admin, companyId is auto-set on backend, so we don't send it
      const guideData = {
        email: values.email,
        username: values.username,
        fullName: values.fullName || undefined,
        phoneNumber: values.phoneNumber || undefined,
        isActive: values.isActive,
      };

      await createGuide(guideData).unwrap();
      navigate("/guides", { state: { refresh: true } });
    } catch (error) {
      console.error("Failed to create guide:", error);
      setStatus({
        message:
          error?.data?.message || "Failed to create user. Please try again.",
      });
      setSubmitting(false);
    }
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        alignSelf: "stretch",
        minHeight: "100vh",
        paddingTop: { xs: 2, sm: 2.5, md: 3 },
        paddingLeft: { xs: 1.5, sm: 2, md: 3 },
        paddingRight: { xs: 1.5, sm: 2, md: 3 },
        paddingBottom: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit, isSubmitting, status, values, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Header */}
              <Box>
                <Typography
                  sx={{
                    color: delftBlue[500],
                    fontSize: { xs: 22, sm: 24, md: 28 },
                    fontFamily: "Audiowide, sans-serif",
                    fontWeight: 400,
                    lineHeight: 1.29,
                    marginBottom: 1,
                  }}
                >
                  Invite User
                </Typography>
                <Typography
                  sx={{
                    color: delftBlue[500],
                    fontSize: { xs: 13, sm: 14, md: 16 },
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 400,
                    lineHeight: 1.5,
                  }}
                >
                  Onboard a new user for your company
                </Typography>
              </Box>

              {/* Error Message */}
              {status?.message && (
                <Alert severity="error">{status.message}</Alert>
              )}

              {/* Form Fields */}
              <Stack spacing={2}>
                <FormInput
                  name="email"
                  label="Email *"
                  placeholder="user@example.com"
                  type="email"
                />
                <FormInput
                  name="username"
                  label="Username *"
                  placeholder="user_username"
                />
                <FormInput
                  name="fullName"
                  label="Full Name"
                  placeholder="John Doe"
                />
                <FormInput
                  name="phoneNumber"
                  label="Phone Number"
                  placeholder="04XXXXXXXX"
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingY: 1,
                  }}
                >
                  <Typography
                    sx={{
                      color: delftBlue[500],
                      fontSize: { xs: 14, sm: 15 },
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    Active Status
                  </Typography>
                  <Switch
                    checked={values.isActive}
                    onChange={(e) =>
                      setFieldValue("isActive", e.target.checked)
                    }
                    sx={getSwitchStyles(false)}
                  />
                </Box>
              </Stack>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate("/guides")}
                  disabled={isSubmitting || isCreating}
                  sx={{
                    borderRadius: 2,
                    borderColor: delftBlue[200],
                    color: delftBlue[500],
                    textTransform: "none",
                    paddingX: 3,
                    paddingY: 1,
                    "&:hover": {
                      borderColor: delftBlue[300],
                      background: delftBlue[50],
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || isCreating}
                  sx={{
                    borderRadius: 2,
                    background: delftBlue[500],
                    textTransform: "none",
                    paddingX: 3,
                    paddingY: 1,
                    "&:hover": {
                      background: delftBlue[600],
                    },
                    "&:disabled": {
                      background: delftBlue[300],
                    },
                  }}
                >
                  {isSubmitting || isCreating ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  ) : (
                    "Invite User"
                  )}
                </Button>
              </Stack>
            </Stack>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default GuideFormPage;
